import React, { useState } from 'react';
import HamburgerMenu from '../global/menu';
import '../global/styles.css';

function IVMedicationCalculator({ userInfo, isAdmin, onLogout, onNavigate }) {
  // IV Calculator State
  const [dosingMethod, setDosingMethod] = useState('infusion');
  const [adminTime, setAdminTime] = useState('');
  const [timeUnit, setTimeUnit] = useState('minutes');
  const [desiredDose, setDesiredDose] = useState('');
  const [doseUnit, setDoseUnit] = useState('mg');
  const [concentration, setConcentration] = useState('');
  const [manualFlowRate, setManualFlowRate] = useState('');
  const [dropFactor, setDropFactor] = useState('10');
  const [flowRate, setFlowRate] = useState('---');
  const [dripRate, setDripRate] = useState('---');

  // Unit Converter State
  const [inputValue, setInputValue] = useState('');
  const [fromUnit, setFromUnit] = useState('mg');
  const [toUnit, setToUnit] = useState('mg');
  const [convertedValue, setConvertedValue] = useState('---');

  const handleCalculateIV = () => {
    const dropFactorValue = parseFloat(dropFactor);
    let calculatedFlowRate;

    // Attempt to calculate flow rate from inputs
    let dose = parseFloat(desiredDose);
    const conc = parseFloat(concentration);
    let time = parseFloat(adminTime);

    // Convert time to hours
    if (timeUnit === 'minutes') {
      time = time / 60;
    }

    // Calculate flow rate if all inputs are provided
    if (!isNaN(dose) && !isNaN(conc) && !isNaN(time) && time > 0 && conc > 0) {
      // Convert dose to mg
      if (doseUnit === 'g') dose *= 1000;
      if (doseUnit === 'mcg') dose /= 1000;

      calculatedFlowRate = (dose / conc) / time;
    } else {
      // Use manually entered flow rate
      calculatedFlowRate = parseFloat(manualFlowRate);
    }

    // Validate flow rate
    if (isNaN(calculatedFlowRate) || calculatedFlowRate <= 0) {
      alert('Please enter valid inputs for dose or flow rate.');
      return;
    }

    // Calculate drip rate (drops/min)
    const calculatedDripRate = (calculatedFlowRate * dropFactorValue) / 60;

    // Display Results
    setFlowRate(calculatedFlowRate.toFixed(2) + ' mL/hr');
    setDripRate(calculatedDripRate.toFixed(2) + ' drops/min');
  };

  const handleConvertUnits = () => {
    let value = parseFloat(inputValue);

    // Validate input
    if (isNaN(value)) {
      alert('Please enter a valid number to convert.');
      return;
    }

    // Conversion Logic
    if (fromUnit === 'mg' && toUnit === 'g') value /= 1000;
    else if (fromUnit === 'g' && toUnit === 'mg') value *= 1000;
    else if (fromUnit === 'mcg' && toUnit === 'mg') value /= 1000;
    else if (fromUnit === 'mg' && toUnit === 'mcg') value *= 1000;
    else if (fromUnit === 'mcg' && toUnit === 'g') value /= 1000000;
    else if (fromUnit === 'g' && toUnit === 'mcg') value *= 1000000;
    else if (fromUnit === toUnit) {
      // No conversion needed
    } else {
      alert('Unsupported unit conversion.');
      return;
    }

    // Display Converted Value with Unit
    setConvertedValue(`${value.toFixed(2)} ${toUnit}`);
  };

  return (
    <div className="container-center" style={{ paddingBottom: '80px' }}>
      {/* Hamburger Menu */}
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div style={{ maxWidth: '900px', width: '100%' }}>
        {/* IV Medication Drip/Infusion Calculator Card */}
        <div className="card" style={{ marginBottom: '30px', padding: '40px' }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#0E3293',
            margin: '0 0 10px 0',
            textAlign: 'center'
          }}>
            IV Medication Drip/Infusion Calculator
          </h2>
          <p style={{
            textAlign: 'center',
            color: '#777',
            marginBottom: '30px',
            fontSize: '15px'
          }}>
            Choose a method to calculate the flow rate or manually enter it
          </p>

          {/* Calculation Section */}
          <div style={{
            background: '#f8f9fa',
            padding: '25px',
            borderRadius: '12px',
            marginBottom: '25px'
          }}>
            <h3 style={{
              fontSize: '18px',
              color: '#0E3293',
              marginBottom: '20px',
              fontWeight: '600'
            }}>
              Calculate Flow Rate (mL/hr)
            </h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Dosing Method:
              </label>
              <select
                value={dosingMethod}
                onChange={(e) => setDosingMethod(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="infusion">Infusion Dosing (mg/hr)</option>
                <option value="weightBased">Weight-Based Dosing (mg/kg/hr)</option>
              </select>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Desired Timeframe:
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="number"
                  value={adminTime}
                  onChange={(e) => setAdminTime(e.target.value)}
                  placeholder="Enter time"
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <select
                  value={timeUnit}
                  onChange={(e) => setTimeUnit(e.target.value)}
                  style={{
                    padding: '10px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Desired Dose:
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="number"
                  value={desiredDose}
                  onChange={(e) => setDesiredDose(e.target.value)}
                  placeholder="Enter dose"
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <select
                  value={doseUnit}
                  onChange={(e) => setDoseUnit(e.target.value)}
                  style={{
                    padding: '10px',
                    border: '2px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="mg">mg</option>
                  <option value="mcg">mcg</option>
                  <option value="g">g</option>
                  <option value="meq">mEq</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '0' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Concentration (mg/mL):
              </label>
              <input
                type="number"
                value={concentration}
                onChange={(e) => setConcentration(e.target.value)}
                placeholder="Enter concentration"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Manual Entry Section */}
          <div style={{
            background: '#f8f9fa',
            padding: '25px',
            borderRadius: '12px',
            marginBottom: '25px'
          }}>
            <h3 style={{
              fontSize: '18px',
              color: '#0E3293',
              marginBottom: '15px',
              fontWeight: '600'
            }}>
              Manual Flow Rate Entry (mL/hr)
            </h3>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              Flow Rate (mL/hr):
            </label>
            <input
              type="number"
              value={manualFlowRate}
              onChange={(e) => setManualFlowRate(e.target.value)}
              placeholder="Enter flow rate"
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Drop Factor */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              Drop Factor (gtt/mL):
            </label>
            <select
              value={dropFactor}
              onChange={(e) => setDropFactor(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="10">10 gtt/mL</option>
              <option value="15">15 gtt/mL</option>
              <option value="20">20 gtt/mL</option>
              <option value="60">60 gtt/mL</option>
            </select>
          </div>

          {/* Calculate Button */}
          <button
            onClick={handleCalculateIV}
            className="btn btn-primary btn-full"
            style={{
              background: '#0E3293',
              borderColor: '#0E3293',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '25px'
            }}
          >
            Calculate
          </button>

          {/* Results */}
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <div style={{
              flex: 1,
              minWidth: '200px',
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '2px solid #e0e0e0',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666', fontWeight: '500' }}>
                Flow Rate:
              </p>
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#0E3293' }}>
                {flowRate}
              </span>
            </div>
            <div style={{
              flex: 1,
              minWidth: '200px',
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '2px solid #e0e0e0',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666', fontWeight: '500' }}>
                Drip Rate:
              </p>
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#0E3293' }}>
                {dripRate}
              </span>
            </div>
          </div>
        </div>

        {/* Unit Conversion Tool Card */}
        <div className="card" style={{ marginBottom: '30px', padding: '40px' }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#0E3293',
            margin: '0 0 10px 0',
            textAlign: 'center'
          }}>
            Unit Conversion Tool
          </h2>
          <p style={{
            textAlign: 'center',
            color: '#777',
            marginBottom: '30px',
            fontSize: '15px'
          }}>
            Convert between mg, mcg, g, and mEq
          </p>

          <div style={{
            background: '#f8f9fa',
            padding: '25px',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Enter Value:
              </label>
              <input
                type="number"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Enter value"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                From Unit:
              </label>
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="mg">mg</option>
                <option value="mcg">mcg</option>
                <option value="g">g</option>
                <option value="meq">mEq</option>
              </select>
            </div>

            <div style={{ marginBottom: '0' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                To Unit:
              </label>
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="mg">mg</option>
                <option value="mcg">mcg</option>
                <option value="g">g</option>
                <option value="meq">mEq</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleConvertUnits}
            className="btn btn-primary btn-full"
            style={{
              background: '#0E3293',
              borderColor: '#0E3293',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '20px'
            }}
          >
            Convert
          </button>

          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            border: '2px solid #e0e0e0',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666', fontWeight: '500' }}>
              Converted Value:
            </p>
            <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#0E3293' }}>
              {convertedValue}
            </span>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <footer style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        background: '#ffffff',
        textAlign: 'center',
        padding: '15px 10px',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.08)',
        zIndex: 9999,
        borderTop: '1px solid #e0e0e0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <img
            src="https://us.api.fliplet.com/v1/media/files/5174683/contents/blob"
            alt="NarcoticTrack Logo"
            style={{
              height: '32px',
              verticalAlign: 'middle'
            }}
          />
          <span style={{
            fontSize: '14px',
            color: '#666',
            fontWeight: '400'
          }}>
            © 2025 NarcoticTrack Inventory System, LLC
          </span>
        </div>
      </footer>
    </div>
  );
}

export default IVMedicationCalculator;
